// Shared constants for fund opportunity admin components

export const FundInvestorStatus = {
  CANDIDATE: 'CANDIDATE',
  LEAD: 'LEAD',
  OPPORTUNITY: 'OPPORTUNITY',
  COMMITTED_INVESTOR: 'COMMITTED_INVESTOR',
} as const;

export type FundInvestorStatus = (typeof FundInvestorStatus)[keyof typeof FundInvestorStatus];

export const STATUS_LABELS: Record<string, string> = {
  // Fund Opportunity statuses (following StatusStepper convention)
  NEW: 'New',
  APPROVED: 'Approved',
  EOI: 'EOI',
  APPLICATION_OPEN: 'Applications Open',
  UNDER_MANAGEMENT: 'Under Management',
  COMPLETE: 'Complete',
  // Investor status labels
  CANDIDATE: 'Candidate',
  LEAD: 'Lead',
  OPPORTUNITY: 'Opportunity',
  COMMITTED_INVESTOR: 'Committed Investor',
};

// Complete fund opportunity status progression (matches StatusStepper)
export const FUND_STATUS_PROGRESSION = [
  'NEW',
  'APPROVED',
  'EOI',
  'APPLICATION_OPEN',
  'UNDER_MANAGEMENT',
  'COMPLETE',
] as const;

/**
 * STATUS STEPPER VISUAL CONVENTIONS
 *
 * The StatusStepper follows these visual patterns for different status states:
 *
 * 🔘 COMPLETED/PAST (NEW, APPROVED):
 *    - Greyed out (opacity: 0.5)
 *    - Non-clickable
 *    - Faded appearance
 *
 * 🔵 CURRENT (EOI in the example):
 *    - Blue background (Palette.active: #003366)
 *    - White text, bold font weight
 *    - Active border highlight
 *    - Prominent visual emphasis
 *
 * ⚪ NEXT (APPLICATIONS OPEN):
 *    - Light/transparent background
 *    - Dotted border (2px dotted)
 *    - Clickable for promotion
 *    - Hover effects enabled
 *
 * 🟢 FUTURE (UNDER_MANAGEMENT):
 *    - Progressive green tones (tone2, tone3, tone4, tone5)
 *    - Reduced opacity (0.5)
 *    - Non-clickable
 *    - Visual indication of planned progression
 *
 * ⚪ FINAL (COMPLETE):
 *    - Default grey tone
 *    - Non-clickable endpoint
 *    - Goal destination appearance
 */
export const STATUS_VISUAL_CONVENTIONS = {
  COMPLETED_PAST: ['NEW', 'APPROVED'],
  CURRENT: 'EOI', // Example - determined dynamically
  NEXT: 'APPLICATION_OPEN', // Example - determined dynamically
  FUTURE: ['UNDER_MANAGEMENT'],
  FINAL: 'COMPLETE',
} as const;

// Legacy - kept for backward compatibility but use FUND_STATUS_PROGRESSION for new code
export const PROMOTION_STAGES = ['NEW', 'APPROVED', 'EOI', 'APPLICATION_OPEN'] as const;
